const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN
const FORCE_MIXPANEL = import.meta.env.VITE_ENABLE_MIXPANEL === 'true'

// `initialized` means init was requested and allowed (token present, not
// localhost). `mixpanel` is the SDK once the dynamic import resolves; calls
// made in between are queued and replayed in order.
let initialized = false
let mixpanel = null
const pending = []

function isLocalDev() {
  const host = window.location.hostname
  return host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0'
}

function withMixpanel(fn) {
  if (!initialized) return
  if (mixpanel) {
    fn(mixpanel)
  } else {
    pending.push(fn)
  }
}

/**
 * Loads the Mixpanel SDK on demand. mixpanel-browser is ~1 MB of source, so
 * it is kept out of the entry chunk and fetched when main.jsx calls this
 * after the app has rendered.
 */
export function initMixpanel() {
  if (initialized || !MIXPANEL_TOKEN) return
  if (isLocalDev() && !FORCE_MIXPANEL) {
    console.log('Mixpanel disabled on localhost (set VITE_ENABLE_MIXPANEL=true to override)')
    return
  }
  initialized = true

  import('mixpanel-browser')
    .then((mod) => {
      const sdk = mod.default
      sdk.init(MIXPANEL_TOKEN, {
        debug: FORCE_MIXPANEL,
        ignore_dnt: true,
        track_pageview: "full-url",
        persistence: "localStorage",
        autotrack: {
          pageview: "full-url",
          click: true,
          // BEHAVIOUR CHANGE (2026-10-09, perf work): input and scroll
          // autotrack were previously true. They attach listeners to every
          // field and scroll frame, which hurt the main thread on mobile.
          // Mixpanel no longer records "Form field changed" / "Scrolled"
          // autocaptured events; explicit trackEvent() calls are unaffected.
          input: false,
          scroll: false,
          submit: true,
          capture_text_content: false,
        },
        ip: true,
        loaded: () => {
          console.log("Mixpanel initialized");
        },
      })
      mixpanel = sdk
      while (pending.length) pending.shift()(sdk)
    })
    .catch((err) => {
      console.warn('Mixpanel failed to load', err)
      initialized = false
      pending.length = 0
    })
}

export function identifyUser(userId, properties = {}) {
  withMixpanel((mp) => {
    mp.identify(String(userId))
    if (Object.keys(properties).length > 0) {
      mp.people.set(properties)
    }
  })
}

export function trackEvent(eventName, properties = {}) {
  withMixpanel((mp) => mp.track(eventName, properties))
}

export function resetMixpanel() {
  withMixpanel((mp) => mp.reset())
}
